/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-get, ember/require-computed-property-dependencies, no-prototype-builtins, no-restricted-properties, warp-drive/no-legacy-request-patterns */
// Extends
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';

// Ember Imports
import EmberObject, { get, action, computed } from '@ember/object';
import { sort, setDiff } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';

// Internal Imports
import config from 'linced-admin/config/environment';
import NzService from 'linced-admin/enums/enrolment-nz-service';

// External Imports

import CountryCode from '@linc-technologies/linced-common/enums/country-code';
import Feature from '@linc-technologies/linced-common/enums/feature';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import SharingLevel from '@linc-technologies/linced-common/enums/media-sharing-level';
import {
  byKeys,
  filterBy,
  findBy,
  mapBy,
  sortBy,
  uniq,
  uniqBy,
} from '@linc-technologies/linced-common/utils/arrays';
import { hash } from '@linc-technologies/linced-common/utils/promise';
import { rollbackRecord } from '@linc-technologies/linced-common/utils/record-rollback';

const FEATURE_PACKS = [
  Feature.PAGES_POWER_UP,
  Feature.GOALS_POWER_UP,
  Feature.REPORTING_POWER_UP,
  Feature.MILESTONES_POWER_UP,
  Feature.COMMUNICATIONS_KIT,
  Feature.ADMINISTRATION_KIT,
];

export default class SchoolsController extends Controller {
  // Services
  @service
  fetch;

  @service
  media;

  @service
  session;

  @service
  store;

  @service
  toaster;

  Feature = Feature;
  Jurisdiction = Jurisdiction;
  FEATURE_PACKS = FEATURE_PACKS;

  @tracked
  school = null;

  @tracked
  schoolLogoMedia = '';

  @tracked
  schoolJurisdiction = '';

  @action
  setSchoolJurisdiction(value) {
    this.schoolJurisdiction = value;
  }

  @tracked
  schoolIsCharter = false;

  @action
  setSchoolIsCharter(value) {
    this.schoolIsCharter = value;
  }

  @tracked
  schoolIsPrivate = false;

  @action
  setSchoolIsPrivate(value) {
    this.schoolIsPrivate = value;
  }

  @tracked
  schoolJurisdictionalId = '';

  @action
  setSchoolJurisdictionalId(value) {
    this.schoolJurisdictionalId = value;
  }

  // schoolExternalId is an external ID for the school, such as a SIF RefID.
  @tracked
  schoolExternalId = '';

  // schooljurisdictionalCredential provides a single credential for now, as we
  // only support DEX currently.
  @tracked
  schooljurisdictionalCredential = null;

  // schooljurisdictionalIntegrationPassword provides the password for
  // integration with educational jurisdiction services.
  @tracked
  schooljurisdictionalIntegrationPassword = '';

  @action
  setSchooljurisdictionalIntegrationPassword(value) {
    this.schooljurisdictionalIntegrationPassword = value;
  }

  @tracked
  schoolCountry = CountryCode.values.find((c) => c.value === 169); // Default to NZ

  @action
  setSchoolCountry(value) {
    this.schoolCountry = value;
  }

  @tracked
  schoolName = '';

  @action
  setSchoolName(value) {
    this.schoolName = value;
  }

  @tracked
  schoolDomainName = '';

  @action
  setSchoolDomainName(value) {
    this.schoolDomainName = value;
  }

  @tracked
  schoolEmailAddress = '';

  @action
  setSchoolEmailAddress(value) {
    this.schoolEmailAddress = value;
  }

  @tracked
  schoolPhysicalAddress = null;

  @tracked
  schoolPostalAddress = null;

  @tracked
  schoolId = '';

  @tracked
  schoolGroups = [];

  @tracked
  schoolLogo = '';

  @setDiff('model.optionSets', 'school.optionSetsArray')
  remainingOptionSets;

  @setDiff('model.optionSets', 'selectedOptionSets')
  remainingWizardOptionSets;

  @computed('model.peopleSchemaTemplates.[]')
  get studentTemplates() {
    return this.get('model.peopleSchemaTemplates').filter(
      (t) => t.personType === 1,
    );
  }

  @computed('model.templates.[]')
  get staffTemplates() {
    return this.get('model.peopleSchemaTemplates').filter(
      (t) => t.personType === 2,
    );
  }

  @computed('model.templates.[]')
  get caregiverTemplates() {
    return this.get('model.peopleSchemaTemplates').filter(
      (t) => t.personType === 3,
    );
  }

  @computed('model.templates.[]')
  get doctorsAndSpecialistsTemplates() {
    return this.get('model.peopleSchemaTemplates').filter(
      (t) => t.personType === 4,
    );
  }

  @tracked
  selectedFieldTemplates = [];

  @tracked
  selectedPageTemplates = [];

  @tracked
  selectedOptionSets = [];

  @tracked
  selectedGoalSubjects = [];

  @tracked
  selectedMilestoneSet = null;

  @action
  setSelectedMilestoneSet(value) {
    this.selectedMilestoneSet = value;
  }

  @tracked
  selectedAutogroups = [];

  @tracked
  selectedEnrolmentSchema = null;

  @action
  setSelectedEnrolmentSchema(value) {
    this.selectedEnrolmentSchema = value;
  }

  @tracked
  selectedGroups = [];

  @setDiff('model.groups', 'selectedGroups')
  remainingGroups;

  @tracked
  wizardOpen = false;

  @tracked
  wizardWorking = false;

  @tracked
  wizardProgress = [];

  @tracked
  schoolDialogOpen = false;

  @tracked
  saveInProgress = false;

  @tracked
  errorMessage = '';

  // Filtering of subcomponents by usage sets
  usageSetsSortOrder = Object.freeze(['name']);

  @sort('model.usageSets', 'usageSetsSortOrder')
  sortedUsageSets;

  @tracked
  selectedUsageSetsFilter = [];

  @setDiff('sortedUsageSets', 'selectedUsageSetsFilter')
  remainingUsageSetsFilter;

  @computed('model.pageTemplates.[]', 'selectedUsageSetsFilter.[]')
  get filteredPageTemplates() {
    return this.get('model.pageTemplates')
      .filter((page) => {
        if (!isEmpty(this.selectedUsageSetsFilter)) {
          return this.selectedUsageSetsFilter.some((usageSet) =>
            page.get('usageSetsArray').includes(usageSet),
          );
        }
        return true;
      })
      .sort(byKeys('label'));
  }

  @computed('model.goalSubjects.[]', 'selectedUsageSetsFilter.[]')
  get filteredGoalSubjects() {
    return this.get('model.goalSubjects')
      .filter((goalSubject) => {
        if (!isEmpty(this.selectedUsageSetsFilter)) {
          return this.selectedUsageSetsFilter.some((usageSet) =>
            goalSubject.get('usageSetsArray').includes(usageSet),
          );
        }
        return true;
      })
      .sort(byKeys('label'));
  }

  @computed('model.milestoneSets.[]', 'selectedUsageSetsFilter.[]')
  get filteredMilestoneSets() {
    return this.get('model.milestoneSets')
      .filter((milestoneSets) => {
        if (!isEmpty(this.selectedUsageSetsFilter)) {
          return this.selectedUsageSetsFilter.some((usageSet) =>
            milestoneSets.get('usageSetsArray').includes(usageSet),
          );
        }
        return true;
      })
      .sort(byKeys('label'));
  }

  @computed('model.autogroups.[]', 'selectedUsageSetsFilter.[]')
  get filteredAutogroups() {
    return this.get('model.autogroups')
      .filter((group) => {
        if (!isEmpty(this.selectedUsageSetsFilter)) {
          return this.selectedUsageSetsFilter.some((usageSet) =>
            group.get('usageSetsArray').includes(usageSet),
          );
        }
        return true;
      })
      .sort(byKeys('name'));
  }

  @computed('filteredAutogroups.@each.grouping')
  get autogroupGroupings() {
    return uniq(mapBy(sortBy(this.filteredAutogroups, 'grouping'), 'grouping'));
  }

  @computed('selectedAutogroups.@each.grouping')
  get selectedAutogroupGroupings() {
    return uniq(mapBy(sortBy(this.selectedAutogroups, 'grouping'), 'grouping'));
  }

  // sifZones determines the applicable SIF Zone(s) for the selected country and
  // jurisdiction, if one exists.
  @computed('schoolCountry', 'schoolJurisdiction', 'model.sifZones')
  get sifZones() {
    if (isEmpty(this.schoolCountry) || isEmpty(this.schoolJurisdiction)) {
      return null;
    }

    let zones = this.model.sifZones;
    if (!zones.hasOwnProperty(this.schoolCountry.value)) {
      // No SIF zones for the selected country.
      return null;
    }

    return filterBy(
      zones[this.schoolCountry.value],
      'jurisdiction',
      this.schoolJurisdiction.value,
    );
  }

  // selectedSifZone is the SIF zone selected by the user.
  @tracked
  selectedSifZone = null;

  @action
  setSelectedSifZone(value) {
    this.selectedSifZone = value;
  }

  // sifZone is the SIF zone to be searched.
  get sifZone() {
    return this.selectedSifZone || this.sifZones?.[0];
  }

  // sifResults holds the search results from a SIF school search.
  @tracked
  sifResults = null;

  // sifError contains an error message if a SIF school search failed.
  @tracked
  sifError = null;

  // sifResultsLoading flags whether a SIF school search is in progress.
  @tracked
  sifResultsLoading = false;

  // sifResultsDialogOpen flags whether the SIF school search results dialog
  // should be open/visible.
  @tracked
  sifResultsDialogOpen = false;

  @tracked
  filterValue = '';

  @action
  setFilterValue(value) {
    this.filterValue = value;
  }

  @computed(
    'filterValue',
    'model.schools.@each.{isNew,name,jurisdictionalId,id}',
    'selectedGroup',
  )
  get filteredSchools() {
    let schools = this.get('model.schools').filter((s) => !s.isNew);
    let group = this.selectedGroup;
    if (!isEmpty(group)) {
      schools = group.schoolsArray.filter((s) => !s.isNew);
    }

    let filterValue = this.filterValue.toLowerCase();
    if (!filterValue.length) {
      return schools;
    } else {
      return schools.filter(function (school) {
        if (school.get('name').toLowerCase().indexOf(filterValue) !== -1) {
          // School name matches filter.
          return true;
        }
        if (school.jurisdictionalId.toLowerCase().indexOf(filterValue) !== -1) {
          // Jurisdictional ID matches filter.
          return true;
        }
        if (school.id === filterValue) {
          // School ID (UUID) matches filter completely (partial matches are ignored to avoid weird results).
          return true;
        }

        // None of the things we check against matched.
        return false;
      });
    }
  }

  @tracked
  selectedSchools = [];

  @computed('filteredSchools.@each.id', 'selectedSchools.@each.id')
  get allSelected() {
    return this.filteredSchools.every((school) =>
      this.selectedSchools.includes(school),
    );
  }

  @tracked
  group = null;
  groupDialogOpen = false;

  @computed('model.groups.@each.{isNew,grouping,name}')
  get groupings() {
    let groupings = mapBy(
      uniqBy(this.model.groups, 'grouping').sort(byKeys('grouping')),
      'grouping',
    );

    return groupings.map((grouping) => {
      return {
        groupName: grouping,
        options: filterBy(this.model.groups, 'grouping', grouping).sort(
          byKeys('name'),
        ),
      };
    });
  }

  @tracked selectedGroup = null;

  @action
  setSelectedGroup(value) {
    this.selectedGroup = value;
  }

  features = Feature.values;
  countries = CountryCode.values;
  jurisdictions = Jurisdiction.values;

  @tracked
  uploading;

  @tracked
  wizardConfirmed;

  @tracked currentStep;

  @action
  setCurrentStep(value) {
    this.currentStep = value;
  }

  /**
   * persistCredential saves the provided credential to the backend.
   *
   * @param {Object} credential - A credential object.
   * @param {integer} service - The integration service the password is being stored for.
   * @param {string} password - The integration service password.
   * @returns {Promise}
   */
  persistCredential(credential, service, password) {
    credential.setIntegrationPassword(service, password);
    return credential
      .save()
      .then(() => {
        this.toaster.success('School integration password saved');
      })
      .catch((err) => {
        this.toaster.error(`Error saving school integration password: ${err}`);
      });
  }

  /**
   * saveIntegrationCredentials creates or updates currently stored integration
   * credentials.
   *
   * @returns {Promise}
   */
  saveIntegrationCredentials() {
    let password = this.schooljurisdictionalIntegrationPassword;
    if (isEmpty(password)) {
      // return early..
      return Promise.resolve();
    }

    if (!isEmpty(this.schooljurisdictionalCredential)) {
      // Perform update to existing credential.
      return this.store
        .findRecord(
          'enrolments/credential',
          this.schooljurisdictionalCredential.id,
        )
        .then((credential) => {
          switch (this.schoolJurisdiction.value) {
            case Jurisdiction.NZ_MOE:
              return this.persistCredential(
                credential,
                NzService.DEX,
                password,
              );

            default:
              return Promise.resolve();
          }
        });
    }

    // Create new Credentials..
    let credential = this.store.createRecord('enrolments/credential', {
      schoolId: this.school,
      enabled: true,
      jurisdiction: this.schoolJurisdiction.value,
    });

    // Push in the required jurisidictional credential and service payload.
    switch (this.schoolJurisdiction.value) {
      case Jurisdiction.NZ_MOE:
        // Bind in NZ Credential fragment.
        return this.persistCredential(credential, NzService.DEX, password);
    }

    return Promise.resolve();
  }

  // toggleDialog opens the school dialog.
  toggleDialog() {
    this.errorMessage = '';
    this.toggleProperty('schoolDialogOpen');
    if (!this.schoolDialogOpen) {
      this.resetForm();
    }
  }

  @action
  toggleSchoolDialog() {
    this.toggleDialog();
  }

  @action
  editSchool(id) {
    hash({
      school: this.store.findRecord('school', id),
      groups: this.store.query('schools/group', { schoolId: id }),
    }).then(({ school, groups }) => {
      this.school = school;
      this.schoolName = school.get('name');
      this.schoolLogoMedia = school.get('logoMedia');
      this.schoolLogo = school.get('logo');

      this.schoolJurisdiction = this.jurisdictions.find(
        (j) => j.value === school.get('jurisdiction'),
      );

      this.schoolIsCharter = school.get('isCharter');
      this.schoolIsPrivate = school.get('isPrivate');
      this.schoolJurisdictionalId = school.get('jurisdictionalId');
      this.schoolExternalId = school.get('externalId');

      this.schoolCountry = this.countries.find(
        (c) => c.value === school.get('countryCode'),
      );

      this.schoolDomainName = school.get('domain');
      this.schoolEmailAddress = school.get('emailAddress');
      // Addresses are fragments, so will need to be copied - but for now,
      // they're not editable via this UI, so we just leave them null and
      // the save process won't update them (unless a SIF search has set
      // them).
      this.schoolPostalAddress = null;
      this.schoolPhysicalAddress = null;
      this.schoolGroups = groups;
      this.selectedSifZone = null;

      // Find integration password entries.
      // Since we only have NZ Moe at the moment...
      // TODO: make smarter once we have more than one jurisdictional integration to worry about e.g. Te Rito?
      let credentials = this.model.credentials.filter(
        (c) => get(c, 'school.id') === id,
      );
      if (credentials.length) {
        let credential = credentials[0];
        if (credential.jurisdiction === Jurisdiction.NZ_MOE) {
          this.schooljurisdictionalCredential = credential;
        }
      } else {
        this.schooljurisdictionalCredential = null;
      }
    });

    this.schoolId = id;
    this.toggleDialog();
  }

  @action
  async saveSchool() {
    this.saveInProgress = true;
    this.errorMessage = '';
    let isNew = isEmpty(this.schoolId);
    let school;

    try {
      school = isNew
        ? this.school
        : await this.store.findRecord('school', this.schoolId);
      school.setProperties({
        name: this.schoolName,
        logoMedia: this.schoolLogoMedia,
        isCharter: this.schoolIsCharter,
        isPrivate: this.schoolIsPrivate,
        jurisdictionalId: this.schoolJurisdictionalId,
        externalId: this.schoolExternalId,
        countryCode: this.get('schoolCountry.value'),
        domain: this.schoolDomainName,
        emailAddress: this.schoolEmailAddress,
      });
      // An existing school's jurisdiction is only filled in, never changed.
      // A jurisdiction of 0 (the Go zero value) is not set.
      if (isNew || !school.jurisdiction) {
        school.set('jurisdiction', this.get('schoolJurisdiction.value'));
      }
      if (!isEmpty(this.schoolPostalAddress)) {
        school.set('postalAddress', this.schoolPostalAddress);
      }
      if (!isEmpty(this.schoolPhysicalAddress)) {
        school.set('physicalAddress', this.schoolPhysicalAddress);
      }

      await school.save();
    } catch (err) {
      // Keep the form so the user can fix it and retry. A new record stays
      // loaded for the retry; rolling it back would unload it.
      this.handleError(err);
      if (school && !school.isNew) {
        rollbackRecord(school);
      }
      return;
    } finally {
      this.saveInProgress = false;
    }

    this.schoolDialogOpen = false;
    this.toaster.success('School saved');
    await this.saveIntegrationCredentials();
    this.resetForm();
  }

  // Option Sets
  @action
  async removeOptionSet(item) {
    let optionSetIds = await this.school.optionSetIds;
    let index = optionSetIds.indexOf(item);
    if (index !== -1) {
      optionSetIds.splice(index, 1);
    }
  }

  @action
  async addOptionSet(item) {
    (await this.school.optionSetIds).push(item);
  }

  // New School wizard
  @action
  openWizard() {
    this.resetForm();
    this.selectedFieldTemplates = [];
    this.selectedPageTemplates = [];
    this.selectedOptionSets = [];
    this.selectedGoalSubjects = [];
    this.selectedMilestoneSet = null;
    this.selectedAutogroups = [];
    this.selectedGroups = [];
    this.wizardConfirmed = false;
    this.wizardWorking = false;
    this.wizardProgress = [];
    this.currentStep = 0;
    this.wizardOpen = true;
  }

  @action
  closeWizard() {
    this.wizardOpen = false;
  }

  @action
  toggleWizardTemplate(propertyKey, template) {
    let selected = this[propertyKey];
    if (selected.includes(template)) {
      this[propertyKey] = selected.filter((item) => item !== template);
    } else {
      this[propertyKey] = [...selected, template];
    }
  }

  @action
  toggleWizardTemplateAll(propertyKey, templates, filters) {
    let selected = this[propertyKey];
    if (!isEmpty(filters)) {
      // Select and de-select only the filtered items
      let updated = [...selected];
      templates.forEach((template) => {
        // If the template is not already in the selected array, add it
        if (!updated.includes(template)) {
          updated.push(template);
          return;
        }
        // If the template is already in the selected array, remove it
        updated = updated.filter((item) => item !== template);
      });
      this[propertyKey] = updated;
      return;
    }
    // No filters are selected, deselect and select all items in the list
    if (selected.length >= templates.length) {
      this[propertyKey] = [];
      return;
    }
    this[propertyKey] = [...templates];
  }

  @action
  removeWizardOptionSet(item) {
    this.selectedOptionSets = this.selectedOptionSets.filter((o) => o !== item);
  }

  @action
  addWizardOptionSet(item) {
    this.selectedOptionSets = [...this.selectedOptionSets, item];
  }

  @action
  removeWizardGroup(item) {
    this.selectedGroups = this.selectedGroups.filter((g) => g !== item);
  }

  @action
  addWizardGroup(item) {
    this.selectedGroups = [...this.selectedGroups, item];
  }

  // searchSif performs a search against the selected Jurisdiction's SIF Zone
  // in order to update a school's details and connect the Hero record to the
  // SIF record via the school.externalId property.
  @action
  searchSif() {
    this.sifResults = [];
    this.sifError = null;
    this.sifResultsLoading = true;
    this.sifResultsDialogOpen = true;

    let url = `${config.APP.apiHost}/providers/au/sif/v4/schools/:search`;
    let params = {
      zoneId: this.sifZone.id,
      stateProvinceId: this.schoolJurisdictionalId,
    };
    this.fetch
      .post(url, {
        dataType: 'json',
        data: JSON.stringify(params),
      })
      .then((response) => {
        this.sifResults = response.schools || [];
      })
      .catch(() => {
        this.sifError = 'SIF search failed.  Please try again.';
      })
      .finally(() => {
        this.sifResultsLoading = false;
      });
  }

  // toggleSifResultsDialog toggles visibility of the SIF school search
  // results dialog.
  @action
  toggleSifResultsDialog() {
    this.toggleProperty('sifResultsDialogOpen');
  }

  // acceptSifResult accepts a SIF school search result, setting the
  // externalId as appropriate.
  @action
  acceptSifResult(result) {
    this.schoolExternalId = get(result, 'externalId');
    this.sifResultsDialogOpen = false;
  }

  @action
  createSchool() {
    this.wizardConfirmed = true;
    this.wizardWorking = true;

    let fetch = this.fetch;
    let jurisdiction =
      this.get('schoolJurisdiction.value') ?? Jurisdiction.NZ_MOE;

    this.wizardProgress = [
      ...this.wizardProgress,
      EmberObject.create({
        id: 'school',
        label: 'Creating school',
        done: false,
      }),
    ];

    let school = this.store.createRecord('school', {
      name: this.schoolName,
      jurisdiction,
      isCharter: this.schoolIsCharter,
      isPrivate: this.schoolIsPrivate,
      jurisdictionalId: this.schoolJurisdictionalId,
      externalId: this.schoolExternalId,
      domain: this.schoolDomainName,
      emailAddress: this.schoolEmailAddress,
      optionSetIds: this.selectedOptionSets,
      countryCode: this.get('schoolCountry.value'),
    });

    school.save().then(
      (school) => {
        // Mark school creation as complete.
        findBy(this.wizardProgress, 'id', 'school').set('done', true);

        // Continue on to apply templates.  Keep going if any individual requests fail.
        // All of these steps run in parallel below (allSettled), each marking
        // its own entry done/error as it finishes - so there's no reason to
        // reveal them one at a time here; every step is already known at
        // this point, so register them all in a single reassignment.
        this.wizardProgress = [
          ...this.wizardProgress,
          EmberObject.create({
            id: 'timetable',
            label: 'Applying timetable structure',
            done: false,
          }),
          EmberObject.create({
            id: 'fields',
            label: 'Applying fields',
            done: false,
          }),
          EmberObject.create({
            id: 'enrolmentSchema',
            label: 'Applying Enrolment Form',
            done: false,
          }),
          EmberObject.create({
            id: 'pages',
            label: 'Applying Pages',
            done: false,
          }),
          EmberObject.create({
            id: 'goals',
            label: 'Applying Goal Subjects',
            done: false,
          }),
          EmberObject.create({
            id: 'milestones',
            label: 'Applying Milestone Sets',
            done: false,
          }),
          EmberObject.create({
            id: 'autogroups',
            label: 'Applying Autogroups',
            done: false,
          }),
          EmberObject.create({
            id: 'groups',
            label: 'Adding to Groups',
            done: false,
          }),
        ];

        Promise.allSettled([
          // Stage 1: Do stuff which doesn't depend on other steps.

          // Add school to any selected Groups
          new Promise((resolve) => {
            if (isEmpty(this.selectedGroups)) {
              findBy(this.wizardProgress, 'id', 'groups').set('done', true);
              return resolve();
            }
            this.selectedGroups
              .reduce((chain, group) => {
                return chain.then(() => {
                  return group.reload().then(async (g) => {
                    (await g.schoolIds).push(school);
                    return g.save();
                  });
                });
              }, Promise.resolve())
              .catch(() => {
                findBy(this.wizardProgress, 'id', 'groups').set(
                  'error',
                  'Failed!',
                );
              })
              .finally(() => {
                findBy(this.wizardProgress, 'id', 'groups').set('done', true);
                return resolve();
              });
          }),

          // Apply default timetable structure
          // TODO: Implement timetable structure templates, for different jurisdictions / school types
          new Promise((resolve) => {
            let periodPromises = [];
            let days = [];

            ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'].forEach(
              (day, dayIdx) => {
                days[dayIdx] = {
                  name: day,
                  isoDayNumber: dayIdx + 1,
                  periodIds: [],
                };
                periodPromises.push(
                  this.store
                    .createRecord('timetable/period', {
                      schoolId: school,
                      shortName: 'AM',
                      longName: 'Morning',
                      duration: 210,
                      startTime: '09:00',
                    })
                    .save()
                    .then((am) => {
                      days[dayIdx].periodIds[0] = am.id;
                    }),
                );
                periodPromises.push(
                  this.store
                    .createRecord('timetable/period', {
                      schoolId: school,
                      shortName: 'PM',
                      longName: 'Afternoon',
                      duration: 90,
                      startTime: '13:30',
                    })
                    .save()
                    .then((pm) => {
                      days[dayIdx].periodIds[1] = pm.id;
                    }),
                );
              },
            );

            resolve(
              Promise.all(periodPromises)
                .then(() => {
                  let structure = this.store.createRecord(
                    'timetable/structure',
                    {
                      schoolId: school,
                      name: 'AM/PM',
                      effectiveFrom: '2000-01-01',
                      days,
                    },
                  );

                  return structure.save();
                })
                .then(
                  () => {
                    findBy(this.wizardProgress, 'id', 'timetable').set(
                      'done',
                      true,
                    );
                  },
                  () => {
                    findBy(this.wizardProgress, 'id', 'timetable').set(
                      'error',
                      'Failed!',
                    );
                  },
                ),
            );
          }),

          // Apply field sets
          new Promise((resolve) => {
            resolve(
              fetch
                .post(
                  config.APP.apiHost + '/people-schema/v4/templates/sets:apply',
                  {
                    dataType: 'json',
                    data: JSON.stringify({
                      schoolId: school.get('id'),
                      setIds: mapBy(this.selectedFieldTemplates, 'id'),
                    }),
                  },
                )
                .then(
                  () => {
                    findBy(this.wizardProgress, 'id', 'fields').set(
                      'done',
                      true,
                    );
                  },
                  () => {
                    findBy(this.wizardProgress, 'id', 'fields').set(
                      'error',
                      'Failed!',
                    );
                  },
                ),
            );
          }),

          // Apply Goal Subject templates
          new Promise((resolve) => {
            let numApplied = 0;
            let numFailed = 0;
            let promiseArray = [];
            let data = {
              schoolId: school.get('id'),
            };
            let options = {
              data: JSON.stringify(data),
            };

            let templatesUri = `${config.APP.apiHost}/goals/${config.APP.apiPath}/templates/subjects`;
            let templates = this.selectedGoalSubjects;
            let numTemplates = templates.get('length');

            templates.forEach((template) => {
              promiseArray.push(
                fetch
                  .post(`${templatesUri}/${template.id}/:apply`, options)
                  .then(
                    () => {
                      numApplied++;
                    },
                    () => {
                      numFailed++;
                    },
                  ),
              );
            });
            resolve(
              Promise.allSettled(promiseArray).then(() => {
                if (numApplied === numTemplates) {
                  findBy(this.wizardProgress, 'id', 'goals').set('done', true);
                } else if (numApplied === 0) {
                  findBy(this.wizardProgress, 'id', 'goals').set(
                    'error',
                    'Failed!',
                  );
                } else {
                  findBy(this.wizardProgress, 'id', 'goals').set(
                    'error',
                    `${numApplied} out of ${numTemplates} applied (${numFailed} failed)`,
                  );
                }
              }),
            );
          }),

          // Apply Autogroup templates
          new Promise((resolve) => {
            let numApplied = 0;
            let numFailed = 0;
            let promiseArray = [];
            let data = {
              schoolId: school.get('id'),
            };
            let options = {
              data: JSON.stringify(data),
            };
            let templatesUri = `${config.APP.apiHost}/groups/${config.APP.apiPath}/templates/groups`;
            let templates = this.selectedAutogroups;
            let numTemplates = templates.get('length');

            templates.forEach((template) => {
              promiseArray.push(
                fetch
                  .post(`${templatesUri}/${template.id}/:apply`, options)
                  .then(
                    () => {
                      numApplied++;
                    },
                    () => {
                      numFailed++;
                    },
                  ),
              );
            });
            resolve(
              Promise.allSettled(promiseArray).then(() => {
                if (numApplied === numTemplates) {
                  findBy(this.wizardProgress, 'id', 'autogroups').set(
                    'done',
                    true,
                  );
                } else if (numApplied === 0) {
                  findBy(this.wizardProgress, 'id', 'autogroups').set(
                    'error',
                    'Failed!',
                  );
                } else {
                  findBy(this.wizardProgress, 'id', 'autogroups').set(
                    'error',
                    `${numApplied} out of ${numTemplates} applied (${numFailed} failed)`,
                  );
                }
              }),
            );
          }),
        ])
          .then(() => {
            // Stage 2 - apply templates which depend on Stage 1 templates
            return Promise.allSettled([
              // Apply a Milestone set
              new Promise((resolve) => {
                let numApplied = 0;
                let numFailed = 0;
                let promiseArray = [];
                let data = {
                  schoolId: school.get('id'),
                };
                let options = {
                  data: JSON.stringify(data),
                };

                let templatesUri = `${config.APP.apiHost}/milestones/${config.APP.apiPath}/templates/sets`;

                let template = this.selectedMilestoneSet;
                let numTemplates = 1;
                promiseArray.push(
                  fetch
                    .post(`${templatesUri}/${template.id}/:apply`, options)
                    .then(() => {
                      numApplied++;
                    })
                    .catch(() => {
                      numFailed++;
                    }),
                );
                resolve(
                  Promise.allSettled(promiseArray).then(() => {
                    switch (numApplied) {
                      case 1: {
                        findBy(this.wizardProgress, 'id', 'milestones').set(
                          'done',
                          true,
                        );
                        break;
                      }
                      case 0: {
                        findBy(this.wizardProgress, 'id', 'milestones').set(
                          'error',
                          'Failed!',
                        );
                        break;
                      }
                      default: {
                        findBy(this.wizardProgress, 'id', 'milestones').set(
                          'error',
                          `${numApplied} out of ${numTemplates} applied (${numFailed} failed)`,
                        );
                      }
                    }
                  }),
                );
              }),

              // Apply Enrolment Form
              new Promise((resolve) => {
                if (isEmpty(this.selectedEnrolmentSchema)) {
                  // No template selected; mark as done and move on.
                  findBy(this.wizardProgress, 'id', 'enrolmentSchema').set(
                    'done',
                    true,
                  );
                  return resolve();
                }

                let data = {
                  schoolId: school.id,
                  countryCode: CountryCode.getLabel(school.countryCode),
                  schoolCode: school.jurisdictionalId,
                  schoolName: school.name,
                  // TODO: We do not store an id on the enrolment schema so we can't do the
                  //  blobby serving. This will work until we nuke the unauthenticated
                  //  media routes.
                  schoolLogoUrl: `${config.APP.apiHost}/media/${config.APP.apiPath}/media/${school.logoMedia}/logo.png}`,
                  jurisdiction,
                };
                let options = {
                  data: JSON.stringify(data),
                };

                let templatesUri = `${config.APP.apiHost}/enrolments/${config.APP.apiPath}/templates/schemas`;
                let templateId = this.get('selectedEnrolmentSchema.id');
                let enrolmentSchemaProgress = findBy(
                  this.wizardProgress,
                  'id',
                  'enrolmentSchema',
                );
                resolve(
                  fetch
                    .post(`${templatesUri}/${templateId}/:apply`, options)
                    .then(
                      () => {
                        enrolmentSchemaProgress.set('done', true);
                      },
                      () => {
                        enrolmentSchemaProgress.set('error', 'Failed!');
                      },
                    ),
                );
              }),
            ]);
          })
          .then(() => {
            // Stage 3 - apply templates which depend on Stage 2 templates
            return Promise.allSettled([
              // Apply Page templates
              new Promise((resolve) => {
                let numApplied = 0;
                let numFailed = 0;
                let promiseArray = [];
                let data = {
                  schoolId: school.get('id'),
                };
                let options = {
                  data: JSON.stringify(data),
                };
                let templatesUri = `${config.APP.apiHost}/posts/${config.APP.apiPath}/templates/pages`;
                let templates = this.selectedPageTemplates;
                let numTemplates = templates.get('length');

                templates.forEach((template) => {
                  promiseArray.push(
                    fetch
                      .post(`${templatesUri}/${template.id}/:apply`, options)
                      .then(
                        () => {
                          numApplied++;
                        },
                        () => {
                          numFailed++;
                        },
                      ),
                  );
                });
                resolve(
                  Promise.allSettled(promiseArray).then(() => {
                    if (numApplied === numTemplates) {
                      findBy(this.wizardProgress, 'id', 'pages').set(
                        'done',
                        true,
                      );
                    } else if (numApplied === 0) {
                      findBy(this.wizardProgress, 'id', 'pages').set(
                        'error',
                        'Failed!',
                      );
                    } else {
                      findBy(this.wizardProgress, 'id', 'pages').set(
                        'error',
                        `${numApplied} out of ${numTemplates} applied (${numFailed} failed)`,
                      );
                    }
                  }),
                );
              }),
            ]);
          })
          .then(() => {
            this.wizardWorking = false;
          });
      },
      () => {
        // School creation failed; we can't apply templates to a school which doesn't exist!
        this.wizardProgress.at(-1).set('error', 'Failed!');
      },
    );
  }

  // Drag-drop logo image uploading
  @action
  startUpload() {
    this.uploading = true;
  }

  @action
  handleUpload(file, upload) {
    this.media.handleImageUpload(
      this,
      file,
      upload,
      SharingLevel.PUBLIC,
      '',
      '',
      'schoolLogoMedia',
      'schoolLogo',
    );
  }

  @action
  handleUploadError() {
    this.toaster.error(`Error uploading logo image!`);
  }

  @action
  toggleFeatureAvailable(feature, checked) {
    if (checked) {
      this.school.featuresAvailable.pushObject(feature);
    } else {
      this.school.featuresAvailable.removeObject(feature);
    }
  }

  @action
  toggleFeatureEnabled(feature, checked) {
    if (checked) {
      this.school.featuresEnabled.pushObject(feature);
    } else {
      this.school.featuresEnabled.removeObject(feature);
    }
  }

  @action
  copyToClipboard(content) {
    navigator.clipboard.writeText(content).then(() => {
      this.toaster.success(`School UUID copied to clipboard`);
    });
    return false;
  }

  @action
  toggleSchool(school, checked) {
    if (checked) {
      this.selectedSchools = [...this.selectedSchools, school];
    } else {
      this.selectedSchools = this.selectedSchools.filter((s) => s !== school);
    }
  }

  @action
  toggleAll() {
    if (this.allSelected) {
      this.selectedSchools = [];
    } else {
      this.selectedSchools = [...this.selectedSchools, ...this.filteredSchools];
    }
  }

  @action
  toggleGroupDialog() {
    if (this.groupDialogOpen) {
      // Closing dialog - reset selection.
      this.selectedSchools = [];
    } else {
      // Opening dialog - create a new group.
      this.group = this.store.createRecord('schools/group');
    }
    this.toggleProperty('groupDialogOpen');
  }

  @action
  addUsageSetFilter(usageSet) {
    this.selectedUsageSetsFilter = [...this.selectedUsageSetsFilter, usageSet];
  }

  @action
  removeUsageSetFilter(usageSet) {
    this.selectedUsageSetsFilter = this.selectedUsageSetsFilter.filter(
      (u) => u !== usageSet,
    );
  }

  @action
  deselectMilestoneSet() {
    this.selectedMilestoneSet = null;
  }

  init() {
    super.init(...arguments);
    this.resetForm();
  }

  handleError(err) {
    if (!isEmpty(err?.errors?.msg)) {
      this.errorMessage = err.errors.msg;
    } else {
      this.errorMessage = err.message;
    }
  }

  resetForm() {
    let school = this.school;
    if (school) {
      // rollback edited optionSetIds as well.
      rollbackRecord(school);
    }
    this.uploading = false;
    this.school = this.store.createRecord('school', {});
    this.schoolId = '';
    this.schoolJurisdiction = '';
    this.schoolIsCharter = false;
    this.schoolIsPrivate = false;
    this.schoolJurisdictionalId = '';
    this.schoolExternalId = '';
    this.schooljurisdictionalCredential = null;
    this.schooljurisdictionalIntegrationPassword = '';
    this.schoolLogoMedia = '';
    this.schoolLogo = '';
    this.schoolName = '';
    this.schoolDomainName = '';
    this.schoolEmailAddress = '';
    this.schoolPhysicalAddress = null;
    this.schoolPostalAddress = null;
    this.schoolCountry = CountryCode.values.find((c) => c.value === 169);
    this.schoolGroups = [];
    this.selectedSifZone = null;
  }
}
